/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useState } from 'react';
import { useMarketingActivityAdapter } from '@/providers/marketing-activity';
import type { MarketingActivityResult } from '@/lib/adapters/marketing-activity-types';
import { createLogger } from '@/lib/logger';

const logger = createLogger();

export interface UseMarketingActivity {
    /** True when an adapter is registered and available. */
    isEnabled: boolean;
    /** True while any of subscribe/checkoutEmail/optInChange is in flight. */
    isPending: boolean;
    /** Footer / general signup. */
    subscribe: (args: { email: string; source?: string }) => Promise<MarketingActivityResult>;
    /** Checkout shipping email entry. */
    checkoutEmail: (args: { email: string; basketId: string }) => Promise<MarketingActivityResult>;
    /** Account marketing-consent toggle change. */
    optInChange: (args: { email: string; optedIn: boolean }) => Promise<MarketingActivityResult>;
}

const NO_ADAPTER_RESULT: MarketingActivityResult = {
    ok: false,
    error: 'Marketing activity adapter is not available',
};

/**
 * Consumer hook that wraps the marketing-activity adapter with a stable surface.
 *
 * Each of `subscribe` / `checkoutEmail` / `optInChange` corresponds to an
 * explicit, shopper-initiated marketing action. The act of taking the action
 * IS the consent grant under GDPR/CCPA — this hook intentionally does NOT
 * read or forward CMP tracking-consent state. CMP governs passive tracking
 * (pixels, analytics), not first-party transactional opt-ins.
 *
 * - All calls are wrapped in try/catch and translate errors to `{ ok: false, error }`.
 * - Callers should switch on `result.ok` instead of try/catch — this hook does not throw.
 * - `isPending` toggles around each call; multiple concurrent calls all set it true,
 *   and it returns to false when the last in-flight call settles.
 */
export function useMarketingActivity(): UseMarketingActivity {
    const adapter = useMarketingActivityAdapter();
    const [pendingCount, setPendingCount] = useState(0);

    const runWithPending = useCallback(
        async <T extends MarketingActivityResult>(call: () => Promise<T>): Promise<T> => {
            setPendingCount((n) => n + 1);
            try {
                return await call();
            } finally {
                setPendingCount((n) => Math.max(0, n - 1));
            }
        },
        []
    );

    const subscribe = useCallback(
        async ({ email, source }: { email: string; source?: string }): Promise<MarketingActivityResult> => {
            if (!adapter) return NO_ADAPTER_RESULT;
            return runWithPending(async () => {
                try {
                    return await adapter.subscribe({ email, source });
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to subscribe';
                    logger.warn('marketing-activity.subscribe failed', { error });
                    return { ok: false, error: message } satisfies MarketingActivityResult;
                }
            });
        },
        [adapter, runWithPending]
    );

    const checkoutEmail = useCallback(
        async ({ email, basketId }: { email: string; basketId: string }): Promise<MarketingActivityResult> => {
            if (!adapter) return NO_ADAPTER_RESULT;
            return runWithPending(async () => {
                try {
                    return await adapter.checkoutEmail({ email, basketId });
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to record checkout email';
                    logger.warn('marketing-activity.checkoutEmail failed', { error });
                    return { ok: false, error: message } satisfies MarketingActivityResult;
                }
            });
        },
        [adapter, runWithPending]
    );

    const optInChange = useCallback(
        async ({ email, optedIn }: { email: string; optedIn: boolean }): Promise<MarketingActivityResult> => {
            if (!adapter) return NO_ADAPTER_RESULT;
            return runWithPending(async () => {
                try {
                    return await adapter.optInChange({ email, optedIn });
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to record opt-in change';
                    logger.warn('marketing-activity.optInChange failed', { error });
                    return { ok: false, error: message } satisfies MarketingActivityResult;
                }
            });
        },
        [adapter, runWithPending]
    );

    return {
        isEnabled: !!adapter,
        isPending: pendingCount > 0,
        subscribe,
        checkoutEmail,
        optInChange,
    };
}
